import { z } from "zod";

/**
 * Form schema for scheduling a book
 * Used in ScheduleBookPage
 */
export const scheduleBookFormSchema = z
    .object({
        /** Author name (required if bookType is authorFocus) */
        author: z.string().optional(),
        /** Book type selection (optional initially, required for submission) */
        bookType: z.enum(["specificBook", "authorFocus", "freeChoice", "tba"]).optional(),
        /** Event month (1-12) */
        month: z.number().int().min(1).max(12),
        /** Selected book ID (required if bookType is specificBook) */
        selectedBookId: z.string().nullable(),
        /** Session dates for the scheduled book */
        sessionDates: z.array(z.date()),
        /** Theme title (optional; for freeChoice type) */
        themeTitle: z.string().max(100).optional(),
        /** Event year */
        year: z.number().int(),
    })
    .refine(
        (data) => {
            // If bookType is not set, form is not yet ready (but not invalid)
            if (!data.bookType) {
                return true;
            }
            // If specificBook, selectedBookId must be provided
            if (data.bookType === "specificBook") {
                return data.selectedBookId !== null && data.selectedBookId.length > 0;
            }
            return true;
        },
        {
            message: "Please select a book",
            path: ["selectedBookId"],
        }
    )
    .refine(
        (data) => {
            // If bookType is not set, form is not yet ready (but not invalid)
            if (!data.bookType) {
                return true;
            }
            // If authorFocus, author must be provided and non-empty
            if (data.bookType === "authorFocus") {
                return data.author !== undefined && data.author.trim().length > 0;
            }
            return true;
        },
        {
            message: "Author is required",
            path: ["author"],
        }
    )
    .refine(
        (data) => {
            // bookType is required for form to be valid
            return data.bookType !== undefined;
        },
        {
            message: "Please select a book type",
            path: ["bookType"],
        }
    )
    .refine(
        (data) => {
            // If bookType is not set, form is not yet ready (but not invalid)
            if (!data.bookType) {
                return true;
            }
            // At least one session date is required
            return data.sessionDates.length > 0;
        },
        {
            message: "At least one session date is required",
            path: ["sessionDates"],
        }
    );

/**
 * TypeScript type for schedule book form data
 */
export type ScheduleBookFormData = z.infer<typeof scheduleBookFormSchema>;
